import 'react-dates/initialize';
import 'react-dates/lib/css/_datepicker.css';

import { Moment } from 'moment';
import React, { useState } from 'react';
import { DateRangePicker as ReactDateRangePicker } from 'react-dates';
import { ICON_AFTER_POSITION } from 'react-dates/constants';
import styled from 'styled-components';

import { colors } from '../../../core';
import Icon from '../Icon';
import Label from '../Label';

export interface DatePickerProps {
  className?: string;
  id?: string;
  disabled?: boolean;
  label?: string;
  name?: string;
  onBlur?: () => void;
  onChange: (arg: { startDate: Moment | null; endDate: Moment | null }) => void;
  onMonthChange?: () => void;
  width?: string;
  value: {
    startDate: Moment | null;
    endDate: Moment | null;
  };
  yearPicker?: any;
  numberOfMonths?: number;
  renderMonthContents?: any;
  futureYears?: number;
  focusedInput?: 'startDate' | 'endDate' | null;
  showPastDays?: boolean;
}

export interface StyledDatePickerProps {
  className?: string;
  name?: string;
  onBlur?: () => void;
  width?: string;
  yearPicker?: any;
}

const PrevIcon = styled(Icon).attrs({
  type: 'caret-left',
  color: colors.primary,
})`
  position: absolute;
  top: 22px;
  left: 22px;
  cursor: pointer;

  &:hover {
    path {
      fill: ${colors.primaryHover};
    }
  }
`;

const NextIcon = styled(Icon).attrs({
  type: 'caret-right',
  color: colors.primary,
})`
  position: absolute;
  top: 22px;
  right: 22px;
  cursor: pointer;

  &:hover {
    path {
      fill: ${colors.primaryHover};
    }
  }
`;

const StyledDateRangePicker = styled.div<StyledDatePickerProps>`
  .DateRangePicker {
    display: block;
    width: ${({ width }) => width || '100%'};
  }

  .DateRangePickerInput {
    display: flex;

    &:hover {
      box-shadow: 0 2px 4px 0 rgba(217, 217, 217, 0.5);
    }

    &__withBorder {
      border-color: ${colors.smoke};
      border-radius: 3px;
    }

    &__disabled {
      background-color: ${colors.pearl};
      border-color: ${colors.cloud};

      &:hover {
        box-shadow: none;
      }

      .DateRangePickerInput_arrow_svg path {
        fill: ${colors.smoke};
      }

      .DateRangePickerInput_calendarIcon {
        cursor: default;
      }
    }
  }

  .DateRangePickerInput_arrow {
    display: inline-flex;
    align-items: center;

    &_svg {
      path {
        fill: ${colors.ash};
      }
    }
  }

  .DateRangePickerInput_calendarIcon {
    line-height: 1;
    margin: 0;
    padding: 6px 10px;

    svg {
      cursor: pointer;
    }
  }

  .DateInput {
    flex: 1;
  }

  .DateInput_input {
    height: 36px;
    font-family: ${({ theme }) => theme.fontFamily || `Quicksand, san-serif`};
    font-size: ${({ theme }) => theme.fontSize || '14px'};
    font-weight: ${({ theme }) => theme.fontWeight || 600};
    line-height: 20px;
    padding: 7px 10px 5px;

    &::placeholder {
      color: ${colors.smoke};
    }

    &__focused {
      border-bottom-color: ${colors.primary};
    }

    &__disabled {
      background-color: ${colors.pearl};
      color: ${colors.smoke};
      font-style: normal;
    }
  }

  .CalendarDay__blocked_out_of_range {
    border: 0;
    color: ${colors.smoke};
  }

  .CalendarDay__selected_span {
    background-color: ${colors.smoke};
    border-color: ${colors.smoke};
    color: ${colors.ash};
  }

  .CalendarDay__selected_start,
  .CalendarDay__selected_end {
    background-color: ${colors.primary};
    border-color: ${colors.primary};
    color: ${colors.white};
  }

  .CalendarDay:hover {
    background-color: ${colors.primaryHover};
    border-color: ${colors.primaryHover};
    color: ${colors.white};
  }

  .CalendarDay__hovered_span {
    background-color: ${colors.smoke};
    border-color: ${colors.smoke};
    color: ${colors.ash};
  }
`;

/**
 * DateRangePicker style wrapper around react-dates library. The implementer of this component must
 * manage start and end date states internally using onChange to update it.
 */
const DateRangePicker = ({
  className,
  disabled,
  label,
  name,
  onBlur,
  onChange,
  width,
  value,
  showPastDays = false,
  ...rest
}: DatePickerProps) => {
  const [focusedInput, setFocusedInput] = useState<'startDate' | 'endDate' | null>(null);

  return (
    <StyledDateRangePicker className={className} name={name} onBlur={onBlur} width={width}>
      {label && <Label>{label}</Label>}
      <ReactDateRangePicker
        {...rest}
        customInputIcon={
          <Icon type="input-calendar" color={disabled ? colors.primaryDisabled : colors.primary} />
        }
        disabled={disabled}
        endDate={value.endDate}
        endDateId="end-date"
        focusedInput={focusedInput}
        hideKeyboardShortcutsPanel={true}
        inputIconPosition={ICON_AFTER_POSITION}
        minimumNights={0}
        navNext={<NextIcon />}
        navPrev={<PrevIcon />}
        onDatesChange={onChange}
        onFocusChange={(focusedInput: any) => setFocusedInput(focusedInput)}
        startDate={value.startDate}
        startDateId="start-date"
        verticalSpacing={0}
        isOutsideRange={showPastDays ? () => false : undefined}
      />
    </StyledDateRangePicker>
  );
};

export default DateRangePicker;
